import {
  Button,
  ButtonGroup,
  DeleteIcon,
  EditIcon,
  ImportIcon,
  Table,
  TableColumns,
  useDefaultSorting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useAdditionalTeachingLoadsListQuery } from '../../../types/planung-graphql-client-defs';
import { formatNumber } from '../../../utils/helper';

export type AdditionalDeputateTableType = {
  uuid: string;
  teacherName: string;
  teacherUuid: string;
  subjectName: string;
  subjectUuid?: string;
  className: string;
  classUuid?: string;
  hours: string;
  hoursRaw: string;
  comment: string;
};

type AdditionalDeputateTableProps = {
  onAdd: () => void;
  onInherit: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  readonly: boolean;
};

export const AdditionalDeputateTable = observer(
  ({ onAdd, onInherit, onEdit, onDelete, readonly }: AdditionalDeputateTableProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const schoolYear = useSelectedSchoolYear();
    const context = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

    const { sorting, onSortingChange } = useDefaultSorting();

    const [{ data }] = useAdditionalTeachingLoadsListQuery({
      variables: { schoolyear: schoolYear?.uuid ?? '' },
      context,
      pause: !schoolYear,
    });

    const tableData: AdditionalDeputateTableType[] = useMemo(() => {
      return (
        data?.additionalTeachingLoads.map((teachingLoad) => ({
          uuid: teachingLoad.uuid,
          teacherName: teachingLoad.teacher.listName ?? '',
          teacherUuid: teachingLoad.teacher.uuid,
          subjectName: teachingLoad.subject?.name ?? '',
          subjectUuid: teachingLoad.subject?.uuid ?? '',
          className: teachingLoad.class?.name ?? '',
          classUuid: teachingLoad.class?.uuid ?? '',
          hours: teachingLoad.hours.toString() ?? '0',
          hoursRaw: teachingLoad.hoursRaw ?? teachingLoad.hours.toString(),
          comment: teachingLoad.comment ?? '',
        })) ?? []
      );
    }, [data]);

    const tableColumns = useMemo((): TableColumns<AdditionalDeputateTableType>[] => {
      return [
        {
          header: t('lesson.table.teacher'),
          id: 'teacherName',
          accessorKey: 'teacherName',
          size: 250,
        },
        {
          header: t('subject.name'),
          id: 'subjectName',
          accessorKey: 'subjectName',
          size: 250,
        },
        {
          header: t('classes.title', { count: 1 }),
          id: 'className',
          accessorKey: 'className',
          size: 250,
        },
        {
          header: t('common.hours'),
          id: 'hours',
          accessorFn: (cell) => formatNumber(parseFloat(cell.hours)),
        },
        {
          header: t('common.comment'),
          id: 'comments',
          accessorKey: 'comment',
          size: 300,
        },
      ];
    }, [t]);

    return (
      <Table<AdditionalDeputateTableType>
        sorting={sorting}
        onSortingChange={onSortingChange}
        showBorderRadius
        showShadow
        canScroll
        minHeight={1000}
        isOnWhite={false}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('deputate.manual')} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
          filename: `${t('deputate.manual')}_${schoolYear?.shortName}`,
        }}
        showActionBar
        actionBarSettings={{
          showExpertFilter: true,
          showPrintButton: true,
          showAddButton: true,
          disableAddButton: readonly,
          extendedActionsRight: (
            <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onInherit} disabled={readonly}>
              {t('common.inherit')}
            </Button>
          ),
        }}
        showSort
        onAddClick={onAdd}
        columns={tableColumns}
        data={tableData}
        lastColWidth='68px'
        lastCol={({ original }) => {
          return (
            <ButtonGroup disabled={readonly}>
              <Button
                hierarchy='secondary'
                onClick={() => onEdit(original.uuid)}
                icon={<EditIcon className='small' />}
                disabled={readonly}
              />
              <Button
                hierarchy='secondary'
                onClick={() => onDelete(original.uuid)}
                icon={<DeleteIcon className='small' />}
                disabled={readonly}
              />
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
